/**
 * ProposalReviewPanel — the "N changes to review" bar at the top of the
 * submission Issues tab, and the review pop-up it opens.
 *
 * Every pending change (a fix, Insert from source, a regenerated section) is
 * reviewed in the review pop-up (``ReviewWorkspace``): over the pane, with
 * Expand into a window over Word. The bar only says how many are waiting and
 * reopens it. Renders nothing when the queue is empty. All logic lives in
 * ``useProposalReviewFlow``; this is the wiring.
 */

import { makeStyles, Spinner } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef } from "react";
import type { SectionIssue } from "../../api/client";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import { type UseProposalReviewFlowResult, useProposalReviewFlow } from "../hooks/useProposalReviewFlow";
import { ReviewWorkspace } from "../review/ReviewWorkspace";
import { useReviewWindow } from "../review/useReviewWindow";

interface ProposalReviewPanelProps {
  runId: string | null;
  /** Bumped by the shell when a resolve pass produces a proposal / finishes. */
  refreshSignal?: number;
  /** Active source library — scopes a Revise rewrite's retrieval. */
  libraryId?: string | null;
  /** Called after each approve/reject so the host refreshes the Issues list. */
  onDecided?: () => void;
  /** Surface a writer-readable error (rendered inline by the host, or here). */
  onError?: (message: string) => void;
  /** The run's issues by id — the cards name each flag they fill. */
  issuesById?: Readonly<Record<string, SectionIssue>>;
  /** Writes approved title-page fields into the open document. */
  writeTitleFields?: (fields: Record<string, string>) => Promise<unknown>;
  /** Bring this proposal's card forward (an issue row's "Change ready ·
   *  Review"). The nonce re-fires a repeat click on the same proposal. */
  focusRequest?: { proposalId: string; nonce: number } | null;
  /** Reports, after every queue change, which issue each pending change fills
   *  (issue id → proposal id) so issue rows can offer "Change ready · Review". */
  onPendingChange?: (pendingByIssue: Record<string, string>) => void;
  /** Bumped by the host to open the review window (a copy just landed, a fix
   *  pass finished). Opens once the queue has something in it. */
  openWindowSignal?: number;
  /** The shell's one review queue. Given, the panel shows it instead of loading
   *  its own (so the Issues band, inline row changes and the Fix window agree). */
  flow?: UseProposalReviewFlowResult;
}

const useStyles = makeStyles({
  root: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    background: surfaceColors.paperDim,
    padding: "10px 12px",
    marginBottom: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink,
  },
  count: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink3,
    fontVariantNumeric: "tabular-nums",
  },
  spacer: { flex: 1 },
  navBtn: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: surfaceColors.paper,
    borderRadius: "4px",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    padding: "3px 9px",
    ":disabled": { opacity: 0.45, cursor: "default" },
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink4,
  },
  openBtn: {
    border: `1px solid ${brandColors.primary}`,
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    borderRadius: "6px",
    height: "32px",
    fontFamily: fonts.body,
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    backgroundColor: surfaceColors.paper,
  },
  undoRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink2,
  },
  undoBtn: {
    border: "none",
    background: "none",
    padding: 0,
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    color: inkColors.ink,
    textDecoration: "underline",
    cursor: "pointer",
  },
  error: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: "var(--danger)",
  },
});

/** The last open signal acted on, across remounts of the Issues tab. */
let lastOpenSignalHandled: number | undefined;

export const ProposalReviewPanel: React.FC<ProposalReviewPanelProps> = ({
  runId,
  refreshSignal,
  libraryId,
  onDecided,
  onError,
  issuesById,
  writeTitleFields,
  focusRequest,
  onPendingChange,
  openWindowSignal,
  flow: sharedFlow,
}) => {
  const styles = useStyles();
  // Hooks run unconditionally: the own queue stays empty (no run) when shared.
  const ownFlow = useProposalReviewFlow({
    runId: sharedFlow ? null : runId,
    refreshSignal,
    libraryId,
    onDecided,
    onError,
    writeTitleFields,
  });
  const flow = sharedFlow ?? ownFlow;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const reviewWindow = useReviewWindow(flow, issuesById);

  // Open on the host's signal — but only once there is something to show, so a
  // signal sent before the queue reloads still lands. The last signal handled
  // is kept outside the component: the Issues tab often mounts right after the
  // signal was sent (Insert from source switches to it), and a ref seeded from
  // the prop would take that signal as already seen.
  const wantOpenRef = useRef(false);
  const seenOpenSignalRef = useRef(lastOpenSignalHandled);
  useEffect(() => {
    if (openWindowSignal === undefined || openWindowSignal === seenOpenSignalRef.current) return;
    seenOpenSignalRef.current = openWindowSignal;
    lastOpenSignalHandled = openWindowSignal;
    wantOpenRef.current = true;
  }, [openWindowSignal]);
  const { open: openWindow, mode: windowMode } = reviewWindow;
  useEffect(() => {
    if (wantOpenRef.current && flow.queue.length > 0 && windowMode === "closed") {
      wantOpenRef.current = false;
      openWindow();
    }
  });

  useEffect(() => {
    if (!onPendingChange) return;
    const byIssue: Record<string, string> = {};
    for (const { proposal } of flow.queue) {
      for (const id of proposal.issue_ids) if (!byIssue[id]) byIssue[id] = proposal.id;
    }
    onPendingChange(byIssue);
  }, [flow.queue, onPendingChange]);

  const { selectById } = flow;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the nonce is the trigger; selectById changes with the queue
  useEffect(() => {
    if (!focusRequest) return;
    if (selectById(focusRequest.proposalId)) openWindow();
  }, [focusRequest?.nonce]);

  // "Change applied · Undo" — for a few seconds after an approval, also once the
  // queue is empty (the review window closes after the last change).
  const undoLine = flow.lastApplied ? (
    <div className={styles.undoRow} role="status">
      <span>Change applied in §{flow.lastApplied.sectionNumber}.</span>
      <button type="button" className={styles.undoBtn} onClick={() => void flow.undoLast()}>
        Undo
      </button>
    </div>
  ) : null;

  // Nothing to review and not mid-load → render nothing (the band only appears
  // when a pass has produced proposals).
  if (flow.queue.length === 0) {
    if (undoLine || flow.error) {
      return (
        <div className={styles.root}>
          {undoLine}
          {flow.error ? <div className={styles.error}>{flow.error}</div> : null}
        </div>
      );
    }
    if (flow.phase === "loading") {
      return (
        <div className={styles.root}>
          <div className={styles.loading}>
            <Spinner size="extra-tiny" />
            <span>Loading proposed changes…</span>
          </div>
        </div>
      );
    }
    return null;
  }

  const active = flow.active;
  const busy = flow.phase === "applying";
  const revising = flow.phase === "revising";

  if (reviewWindow.mode === "overlay") {
    return (
      // biome-ignore lint/a11y/useSemanticElements: a full-pane overlay, not a native modal
      <div className={styles.overlay} role="dialog" aria-label="Review changes">
        <ReviewWorkspace {...reviewWindow} />
      </div>
    );
  }

  const count = flow.queue.length;
  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.headerRow}>
        <span className={styles.title}>
          {count} change{count === 1 ? "" : "s"} to review
        </span>
        {flow.newCount > 0 ? <span className={styles.count}>· {flow.newCount} new</span> : null}
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.openBtn}
          onClick={reviewWindow.open}
          disabled={reviewWindow.mode === "dialog" || busy || revising}
        >
          {reviewWindow.mode === "dialog" ? "Open in its window" : "Review"}
        </button>
      </div>
      {undoLine}
      {flow.error ? <div className={styles.error}>{flow.error}</div> : null}
    </div>
  );
};

export default ProposalReviewPanel;
